import React, { useContext, useEffect, useState } from 'react';
import AppContext from '../../contexts/AppContext';
import { RolesContext } from '../../contexts/RolesContext';
import tenantApi from '../../utils/api/tenant';
import { toast } from 'react-toastify';
import ToggleSmall from '../Shared/Toggle/ToggleSmall';

/**
 * SW2-2541 — "Lock Across All Clients" for previous site map versions.
 *
 * Shown only once SW2 has turned the "Lock Previous Site Map Versions" feature on for the tenant.
 * Off: each client chooses in its own Client Settings. On: previous versions are locked for every
 * client, division and project, and the client setting is overridden.
 *
 * Reads the feature from its own endpoint rather than TenantFeatureContext: Administration
 * deliberately avoids that context (see the SW2-2003 note in Administration.tsx).
 */
const SiteMapVersionLock = () => {
  const [collapse, setCollapse] = useState(true);
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [lockAllClients, setLockAllClients] = useState(false);
  const [busy, setBusy] = useState(false);
  const appStore = useContext(AppContext);
  const user = appStore.get('user');
  // The server requires Administration "all" to change this (tenantController.editMapVersionLock);
  // anyone else would get a live toggle that always fails.
  const canEdit = useContext(RolesContext).userHasPermission('all', 'Administration');

  useEffect(() => {
    if (!user?.tenantId) return;

    let mounted = true;

    tenantApi.mapVersionLock
      .get(user.tenantId)
      .then((res) => {
        if (!mounted) return;
        setFeatureEnabled(!!res?.feature_enabled);
        setLockAllClients(!!res?.lock_all_clients);
      })
      .catch(() => {
        // A failed read leaves the section hidden, which is what "feature off" looks like.
      });

    return () => {
      mounted = false;
    };
  }, [user?.tenantId]);

  const handleToggle = async () => {
    setBusy(true);

    try {
      const res = await tenantApi.mapVersionLock.edit(user.tenantId, !lockAllClients);

      setLockAllClients(!!res?.lock_all_clients);
      toast.success(
        `Previous site map versions ${
          res?.lock_all_clients ? 'locked for all clients' : 'set per client'
        }.`,
      );
    } catch {
      toast.error('Failed to update site map version locking');
    } finally {
      setBusy(false);
    }
  };

  if (!featureEnabled || !canEdit) return null;

  return (
    <div className='settings-tenant-main-container'>
      <div
        className='settings-main-tenant-text'
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
        onClick={() => setCollapse(!collapse)}
      >
        <h3>Site Map Versions</h3>
        <i
          className={collapse ? 'fa fa-chevron-down' : 'fa fa-chevron-up'}
          aria-hidden='true'
        />
      </div>
      <hr />
      <div
        className={
          collapse ? 'administration-hidden' : 'administration-visible'
        }
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h3 style={{ marginTop: '0', marginLeft: '8px' }}>
            Lock Previous Versions Across All Clients
          </h3>
          <ToggleSmall
            isChecked={lockAllClients}
            disabled={busy}
            handleChange={handleToggle}
          />
        </div>
        <p style={{ marginLeft: '8px', color: '#666' }}>
          {lockAllClients
            ? "Legend items cannot be added to or removed from a previous version of any site map, and legend item dates cannot be changed, for every client and division. Admin, Divisions Admin, Regional Manager and Super Admin are exempt."
            : 'Each client chooses in its Client Settings whether previous site map versions are locked.'}
        </p>
      </div>

      <br />
    </div>
  );
};

export default SiteMapVersionLock;
